import React, { useState, useRef, useEffect } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiChevronLeft, FiChevronRight, FiChevronsLeft, FiChevronsRight, FiChevronDown } = FiIcons;

const PaginationControl = ({ currentPage, totalPages, onPageChange }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Generate page buttons logic
  const getPageNumbers = () => {
    const pageNumbers = [];
    
    if (totalPages <= 7) {
      // If less than 7 pages, show all
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push({ type: 'page', number: i });
      }
    } else {
      // Always show first page
      pageNumbers.push({ type: 'page', number: 1 });
      
      if (currentPage <= 3) {
        // Near start: show 1, 2, 3, 4, 5, ..., totalPages
        for (let i = 2; i <= 5; i++) {
          pageNumbers.push({ type: 'page', number: i });
        }
        pageNumbers.push({ type: 'ellipsis' });
        pageNumbers.push({ type: 'page', number: totalPages });
      } else if (currentPage >= totalPages - 2) {
        // Near end: show 1, ..., totalPages-4, totalPages-3, totalPages-2, totalPages-1, totalPages
        pageNumbers.push({ type: 'ellipsis' });
        for (let i = totalPages - 4; i < totalPages; i++) {
          pageNumbers.push({ type: 'page', number: i });
        }
        pageNumbers.push({ type: 'page', number: totalPages });
      } else {
        // Middle: show 1, ..., currentPage-1, currentPage, currentPage+1, ..., totalPages
        pageNumbers.push({ type: 'ellipsis' });
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pageNumbers.push({ type: 'page', number: i });
        }
        pageNumbers.push({ type: 'ellipsis' });
        pageNumbers.push({ type: 'page', number: totalPages });
      }
    }
    
    return pageNumbers;
  };
  
  // Generate all page numbers for dropdown
  const getAllPageNumbers = () => {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  };
  
  return (
    <div className="flex flex-col items-center space-y-4">
      <div className="flex items-center justify-center space-x-1">
        {/* First page button */}
        <button
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1}
          className="p-2 rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          aria-label="First page"
        >
          <SafeIcon icon={FiChevronsLeft} className="text-sm" />
        </button>
        
        {/* Previous button */}
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="p-2 rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          aria-label="Previous page"
        >
          <SafeIcon icon={FiChevronLeft} className="text-sm" />
        </button>
        
        {/* Page numbers */}
        <div className="hidden sm:flex items-center space-x-1">
          {getPageNumbers().map((item, index) => (
            item.type === 'ellipsis' ? (
              <span key={`ellipsis-${index}`} className="px-3 py-2 text-gray-500">
                &hellip;
              </span>
            ) : (
              <button
                key={`page-${item.number}`}
                onClick={() => onPageChange(item.number)}
                className={`px-3 py-2 rounded-md transition-colors ${
                  currentPage === item.number
                    ? 'bg-primary-600 text-white'
                    : 'border border-gray-300 hover:bg-gray-50'
                }`}
              >
                {item.number}
              </button>
            )
          ))}
        </div>
        
        {/* Page dropdown (for mobile and as additional option) */}
        <div className="relative sm:ml-2" ref={dropdownRef}>
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="flex items-center space-x-1 px-3 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50 transition-colors"
          >
            <span className="text-sm">Page {currentPage}</span>
            <SafeIcon icon={FiChevronDown} className={`text-gray-500 transition-transform duration-200 ${isDropdownOpen ? 'transform rotate-180' : ''}`} />
          </button>
          
          {isDropdownOpen && (
            <div className="absolute z-10 mt-1 bg-white rounded-md shadow-lg border border-gray-200 max-h-60 overflow-y-auto w-24 right-0">
              <div className="py-1">
                {getAllPageNumbers().map((page) => (
                  <button
                    key={`dropdown-page-${page}`}
                    onClick={() => {
                      onPageChange(page);
                      setIsDropdownOpen(false);
                    }}
                    className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                      currentPage === page ? 'bg-gray-100 font-medium' : ''
                    }`}
                  >
                    {page}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        
        {/* Next button */}
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="p-2 rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          aria-label="Next page"
        >
          <SafeIcon icon={FiChevronRight} className="text-sm" />
        </button>
        
        {/* Last page button */}
        <button
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage === totalPages}
          className="p-2 rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          aria-label="Last page"
        >
          <SafeIcon icon={FiChevronsRight} className="text-sm" />
        </button>
      </div>
      
      <div className="text-sm text-gray-500">
        Page {currentPage} of {totalPages}
      </div>
    </div>
  );
};

export default PaginationControl;